import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import UserMediaService from '../../../services/UserMediaService';
import { useDaily } from '@daily-co/daily-react';
import { getStoredOrDefaultMediaSettings } from '../../../services/LocalStorage';

export interface CallLocalMediaContextValue {
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  handleEnableMic: (shouldEnable: boolean) => Promise<void>;
  handleEnableCamera: (shouldEnable: boolean) => Promise<void>;
}

const CallLocalMediaContext = createContext<CallLocalMediaContextValue | undefined>(undefined);

export const CallLocalMediaProvider = ({ children }: any) => {
  const callObject = useDaily();
  const [isMicEnabled, setIsMicEnabled] = useState(getStoredOrDefaultMediaSettings().isMicEnabled);
  const [isCameraEnabled, setIsCameraEnabled] = useState(getStoredOrDefaultMediaSettings().isCameraEnabled);

  const handleEnableMic = useCallback(async (shouldEnable: boolean) => {
    const isConnectedToRoom = !!callObject && callObject.meetingState() === 'joined-meeting';
    const localResult = await UserMediaService.shouldEnableMic(shouldEnable, isConnectedToRoom);
    callObject?.setLocalAudio(shouldEnable);
    setIsMicEnabled(localResult);
  }, [callObject]);

  const handleEnableCamera = useCallback(async (shouldEnable: boolean) => {
    const localResult = await UserMediaService.shouldEnableCamera(shouldEnable);
    callObject?.setLocalVideo(shouldEnable);
    setIsCameraEnabled(localResult);
  }, [callObject]);

  const value: CallLocalMediaContextValue = {
    isMicEnabled,
    isCameraEnabled,
    handleEnableMic,
    handleEnableCamera,
  };

  return (
    <CallLocalMediaContext.Provider value={value}>
      {children}
    </CallLocalMediaContext.Provider>
  );
};

export const useCallLocalMedia = () => {
  return useContext(CallLocalMediaContext);
};
